import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, isSuperAdminRateLimitError } from '@/lib/api';
import { toast } from 'sonner';
import { invalidateProjectListQueries } from '@/lib/cache-utils';

export interface VoidInvoicePaymentData {
  payment_id?: string;
  void_reason: string;
  // Super-admin destructive-op guard: when a super-admin confirms the void, the
  // page passes the invoice id here so the DELETE carries the
  // X-Super-Admin-Confirm-Destructive header. Undefined for non-super-admins.
  confirmDestructiveEntityId?: string;
}

export interface RecordPaymentData {
  amount: number;
  payment_date: string;
}

export function useInvoicePayment(invoiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RecordPaymentData) =>
      api.invoices.recordPayment(invoiceId, data),
    onSuccess: async (response) => {
      // CRITICAL: await invalidateQueries to ensure refetch completes BEFORE UI continues
      // Fixes race condition where void payment button isn't visible after payment
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      // v0.14 (Dmitri sweep): also invalidate the Payment History ledger so the
      // Payment History pill / rows refresh after a back-to-back second Record
      // Payment, AND the combined invoice detail query so amount_paid + status
      // badge update without a page reload.
      await queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId, 'payments'] });
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId, 'full'] });
      invalidateProjectListQueries(queryClient);

      // V168: Differentiate full vs partial payment in the toast so Christa sees
      // the system understood her partial payment instead of a generic "recorded".
      const inv = response?.invoice ?? {};
      const fmt = (n: number) => new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', maximumFractionDigits: 2,
      }).format(n);
      if (inv.status === 'partially_paid' && inv.amount_paid != null && inv.total_amount != null) {
        toast.success(`Partial payment recorded (${fmt(Number(inv.amount_paid))} of ${fmt(Number(inv.total_amount))} received)`);
      } else if (inv.status === 'paid') {
        toast.success('Payment recorded — invoice fully paid');
      } else {
        toast.success('Payment recorded successfully');
      }
      return response.invoice;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to record payment');
    },
  });
}

/**
 * V172 — Void a specific payment by payment_id with a required void_reason.
 *
 * On success invalidates:
 *   - ['invoice', invoiceId, 'payments'] — payment ledger rows
 *   - ['invoices'] — invoice list (status may have changed)
 */
export function useVoidInvoicePayment(invoiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VoidInvoicePaymentData) =>
      api.invoices.voidPayment(
        invoiceId,
        {
          void_reason: data.void_reason,
          ...(data.payment_id ? { payment_id: data.payment_id } : {}),
        },
        data.confirmDestructiveEntityId,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId, 'payments'] });
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      // v0.14 (Dmitri sweep): combined invoice detail query also needs to refresh
      // so amount_paid + status badge reflect the void without a page reload.
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId, 'full'] });
      invalidateProjectListQueries(queryClient);
      toast.success('Payment voided successfully');
    },
    onError: (error: any) => {
      // Super-admin destructive-op rate limit (429): clear cooldown message.
      if (isSuperAdminRateLimitError(error)) {
        toast.error('Too many destructive actions — wait a minute and retry.');
        return;
      }
      toast.error(error.message || 'Failed to void payment');
    },
  });
}
